import { createTheme, alpha, darken } from '@mui/material/styles';
import { getPresetById, getBorderRadiusValue } from './themePresets';
import { DARK_GREY, DARK_BLUE } from './darkModePalette';

const buildPalette = (preset, mode) => {
  const isDark = mode === 'dark';
  const primaryMain = preset.primary;

  return {
    mode,
    primary: {
      main: primaryMain,
      light: preset.secondary,
      dark: darken(primaryMain, isDark ? 0.12 : 0.18),
      contrastText: '#ffffff',
    },
    secondary: {
      main: preset.secondary,
      contrastText: '#ffffff',
    },
    success: { main: isDark ? '#34D399' : '#059669' },
    warning: { main: isDark ? '#FBBF24' : '#F59E0B' },
    error: { main: isDark ? '#F87171' : '#DC2626' },
    info: { main: isDark ? '#38BDF8' : '#0284C7' },
    ...(isDark ? { grey: DARK_GREY, blue: DARK_BLUE } : {}),
    background: {
      default: isDark ? '#0F172A' : '#F4F7FB',
      paper: isDark ? '#1E293B' : '#FFFFFF',
    },
    text: {
      primary: isDark ? '#F1F5F9' : '#0F172A',
      secondary: isDark ? '#94A3B8' : '#64748B',
      disabled: isDark ? '#64748B' : '#94A3B8',
    },
    divider: isDark ? alpha('#94A3B8', 0.16) : alpha('#64748B', 0.14),
    action: {
      active: isDark ? '#F1F5F9' : alpha('#0F172A', 0.56),
      hover: isDark ? alpha('#F1F5F9', 0.08) : alpha(preset.primary, 0.06),
      selected: isDark ? alpha(preset.primary, 0.2) : alpha(preset.primary, 0.1),
      disabled: isDark ? alpha('#F1F5F9', 0.3) : alpha('#0F172A', 0.26),
      disabledBackground: isDark ? alpha('#F1F5F9', 0.12) : alpha('#0F172A', 0.12),
    },
  };
};

export const createAppTheme = ({ mode = 'light', paletteId = 'revive', borderRadius = 'medium' } = {}) => {
  const preset = getPresetById(paletteId);
  const radius = getBorderRadiusValue(borderRadius);
  const isDark = mode === 'dark';
  const palette = buildPalette(preset, mode);

  return createTheme({
    palette,
    shape: { borderRadius: radius },
    typography: {
      fontFamily: '"Inter", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      allVariants: {
        color: palette.text.primary,
      },
      h4: { fontWeight: 700, letterSpacing: '-0.02em' },
      h5: { fontWeight: 600, letterSpacing: '-0.01em' },
      h6: { fontWeight: 600 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    shadows: [
      'none',
      isDark ? '0 1px 3px rgba(0,0,0,0.35)' : '0 1px 3px rgba(15,23,42,0.06)',
      isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(15,23,42,0.08)',
      isDark ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px rgba(15,23,42,0.1)',
      ...Array(21).fill(isDark ? '0 12px 32px rgba(0,0,0,0.5)' : '0 12px 32px rgba(15,23,42,0.12)'),
    ],
    brand: {
      accent: preset.accent,
      gradient: preset.gradient,
      presetId: preset.id,
      presetLabel: preset.label,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: {
            colorScheme: isDark ? 'dark' : 'light',
          },
          body: {
            color: palette.text.primary,
            backgroundColor: palette.background.default,
            scrollbarColor: isDark ? '#475569 #1E293B' : '#CBD5E1 #F1F5F9',
            transition: 'background-color 0.25s ease, color 0.25s ease',
          },
          '#root': {
            color: palette.text.primary,
            backgroundColor: palette.background.default,
            minHeight: '100%',
          },
          '*::-webkit-scrollbar': { width: 8, height: 8 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: isDark ? '#475569' : '#CBD5E1',
            borderRadius: 8,
          },
          '*::-webkit-scrollbar-track': {
            backgroundColor: isDark ? '#1E293B' : '#F1F5F9',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
          },
          colorTextSecondary: {
            color: palette.text.secondary,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: radius,
            padding: '8px 18px',
            transition: 'all 0.2s ease',
          },
          containedPrimary: {
            boxShadow: isDark ? 'none' : `0 2px 8px ${alpha(preset.primary, 0.28)}`,
            '&:hover': {
              boxShadow: isDark ? 'none' : `0 4px 14px ${alpha(preset.primary, 0.34)}`,
            },
          },
          outlined: ({ theme }) => ({
            borderColor: isDark ? alpha(theme.palette.primary.main, 0.5) : undefined,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: radius + 2,
            border: `1px solid ${isDark ? alpha('#94A3B8', 0.12) : alpha('#64748B', 0.12)}`,
            boxShadow: isDark ? 'none' : '0 4px 16px rgba(15, 23, 42, 0.06)',
            backgroundImage: 'none',
            color: palette.text.primary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            color: palette.text.primary,
          },
          rounded: { borderRadius: radius },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius,
            color: palette.text.primary,
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? alpha('#94A3B8', 0.3) : alpha('#64748B', 0.35),
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: isDark ? alpha('#CBD5E1', 0.45) : alpha('#64748B', 0.55),
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: preset.primary,
            },
            '&.Mui-focused': {
              boxShadow: `0 0 0 3px ${alpha(preset.primary, 0.2)}`,
            },
          },
          input: {
            color: palette.text.primary,
            '&::placeholder': {
              color: palette.text.secondary,
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
            '&.Mui-focused': {
              color: preset.primary,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
            borderColor: palette.divider,
          },
          head: {
            fontWeight: 600,
            color: palette.text.primary,
            backgroundColor: isDark ? alpha('#F1F5F9', 0.06) : alpha(preset.primary, 0.06),
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: isDark ? alpha('#F1F5F9', 0.04) : alpha(preset.primary, 0.03),
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundImage: 'none',
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            color: isDark ? palette.text.primary : preset.primary,
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
          },
        },
      },
      MuiDialogContentText: {
        styleOverrides: {
          root: {
            color: palette.text.secondary,
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: { color: palette.text.primary },
          secondary: { color: palette.text.secondary },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
            '&:hover': {
              backgroundColor: palette.action.hover,
            },
            '&.Mui-selected': {
              backgroundColor: palette.action.selected,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 500 },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: radius,
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
            border: `1px solid ${isDark ? alpha('#94A3B8', 0.12) : alpha('#64748B', 0.1)}`,
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.45)' : '0 8px 32px rgba(15,23,42,0.12)',
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            borderRadius: `${radius}px !important`,
            color: palette.text.primary,
            backgroundColor: palette.background.paper,
            '&:before': { display: 'none' },
            border: `1px solid ${isDark ? alpha('#94A3B8', 0.12) : alpha('#64748B', 0.12)}`,
            mb: 1,
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
          },
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          paper: {
            backgroundColor: palette.background.paper,
            color: palette.text.primary,
          },
          option: {
            color: palette.text.primary,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          icon: {
            color: palette.text.secondary,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: palette.divider,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            color: palette.text.primary,
          },
        },
      },
    },
  });
};
